import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTaskColumns } from '../../../../hooks/queries/useTaskColumns';
import { TaskItem } from '../../../../types/topic';
import { useTaskUIStore } from '../../../../store/taskUIStore';
import { DroppableTaskColumnContainer } from './DroppableTaskColumnContainer';
import { useTaskHandlers } from '../hooks/useTaskHandlers';
import { logger } from '../../../../utils/logger';
import { AlertCircle, Loader2 } from 'lucide-react';
import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MeasuringStrategy,
  PointerSensor,
  pointerWithin,
  rectIntersection,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { useDndColumn } from './useDndColumn';
import { useTaskMove } from './useTaskMove';
import { useDndHandlers } from './useDndHandlers';
import { useColumnActions } from './useColumnActions';
import { useDragOverlay } from './useDragOverlay';
import { UserFilter } from '../../../dashboard/types/dashboardTypes';
import { DeadlineSortedTaskBoard } from './DeadlineSortedTaskBoard';
import { TaskBoardLayout } from '../layout/TaskBoardLayout';

interface DndTaskBoardProps {
  topicId: string;
  projectId: string;
  tasks: TaskItem[];
  hasCollaborators?: boolean;
  showUnscheduledOnly?: boolean;
  showUnassignedOnly?: boolean;
  userFilters?: UserFilter[];
  sortByDeadline?: boolean;
  onReady?: () => void; // Callback to notify when measurements are complete and board is ready to display
}

export const DndTaskBoard: React.FC<DndTaskBoardProps> = ({
  topicId,
  projectId,
  tasks,
  hasCollaborators = false,
  showUnscheduledOnly,
  showUnassignedOnly,
  userFilters,
  sortByDeadline = false,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  // Task columns from React Query
  const {
    data: columns = [],
    isLoading: isColumnsLoading,
    isError: isColumnsError,
  } = useTaskColumns(topicId);

  // Task state and actions from unified taskUIStore
  const {
    isCreating,
    showDescription,
    newTaskTitle,
    newTaskDescription,
    newTaskTags,
    newTaskDeadline,
    newTaskAssignedTo,
    activeColumn,
    itemToDelete,
    columnToDelete,
    recordingColumn,
    isEditingColumnName,
    isEditingColumnColor,
    setActiveColumn,
    setIsCreating,
    setShowDescription,
    setNewTaskTitle,
    setNewTaskDescription,
    setNewTaskTags,
    setNewTaskDeadline,
    setNewTaskAssignedTo,
    setItemToDelete,
    setColumnToDelete,
    setDragState,
    setDragOverColumn,
    setTargetDropColumnId,
    setIsEditingColumnName,
    resetTaskForm: resetTaskFormState,
  } = useTaskUIStore();

  // Shared task handlers (extracted from component)
  const {
    handleTaskDeletion,
    handleTaskUpdate,
    handleTaskCreation,
    handleVoiceInput,
    isRecording,
  } = useTaskHandlers(topicId);

  // Set the active topic ID in the store for task creation
  useEffect(() => {
    // Important: Use setState to ensure the value is set immediately
    useTaskUIStore.setState({ activeTopicId: topicId });

    // Log for debugging
    logger.component('Setting activeTopicId in TaskUIStore', {
      topicId,
      storeState: useTaskUIStore.getState().activeTopicId,
    });

    // Clean up when component unmounts
    return () => {
      useTaskUIStore.setState({ activeTopicId: undefined });
    };
  }, [topicId]);

  // Column management hooks
  const {
    handleAddColumnAfter,
    handleEditColumnName,
    handleDeleteColumn,
    handleColumnColorChange,
    handleMoveColumnLeft,
    handleMoveColumnRight,
  } = useColumnActions({
    topicId,
    columns,
    setIsEditingColumnName,
    setColumnToDelete,
  });

  // Task movement hooks
  const { moveTask, moveTaskBetweenColumns, forceTaskMove } = useTaskMove(topicId, tasks, columns);

  // Configure DND sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require an 8px movement before activating
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Drag and drop handlers
  const { activeTaskId, activeTask, handleDragStart, handleDragOver, handleDragEnd } =
    useDndHandlers({
      tasks,
      columns,
      moveTask,
      moveTaskBetweenColumns,
      setDragState,
      setDragOverColumn,
      setTargetDropColumnId,
    });

  // Drag overlay configuration
  const dragOverlay = useDragOverlay({ activeTaskId, activeTask });

  // Task handlers (deletion, update, voice) now provided by useTaskHandlers hook

  // Handle task form submission
  const handleSubmit = useCallback(
    async (columnId: TaskItem['status']) => {
      try {
        // Get the current title from taskUIStore
        const currentTitle = newTaskTitle;

        // Log current state before submission
        logger.component('Pre-task creation with explicit data', {
          activeTopicId: topicId,
          title: currentTitle,
          hasTitle: !!currentTitle?.trim(),
          status: columnId,
        });

        // Double check that we have a title
        if (!currentTitle?.trim()) {
          logger.error('Cannot submit task with empty title from component state');
          return;
        }

        // Use shared task creation handler with explicit data
        await handleTaskCreation(columnId, {
          title: currentTitle,
          description: newTaskDescription,
          tags: newTaskTags,
          deadline: newTaskDeadline,
          assignedTo: newTaskAssignedTo,
        });

        // Clear the form state after successful creation
        // Note: createTask() also calls resetTaskForm(), but calling it explicitly ensures consistency
        resetTaskFormState();

        // Keep form open for rapid task entry
        // Set focus back on input for next task
        setTimeout(() => inputRef.current?.focus(), 0);
      } catch (error) {
        // Error handling is now managed by the shared handler
        logger.error('Unexpected error in form submission:', error);
      }
    },
    [
      handleTaskCreation,
      newTaskTitle,
      newTaskDescription,
      newTaskTags,
      newTaskDeadline,
      newTaskAssignedTo,
      resetTaskFormState,
    ]
  );

  // Note: sortByDeadline is passed as a prop from TaskTopic which gets it from context

  // Filter out any legacy status tasks that don't have matching columns
  const validStatuses = useMemo(() => columns.map(col => col.status), [columns]);

  // Filter tasks by valid statuses and sort them by position
  // Use memoization to avoid unnecessary re-computations
  const filteredTasks = useMemo(() => {
    return tasks
      .filter(task => validStatuses.includes(task.status))
      .sort((a, b) => (a.position || 0) - (b.position || 0));
  }, [tasks, validStatuses]);

  // Use a stable key for DndContext to prevent re-initialization that causes layout jumps
  const dndKey = useMemo(() => 'stable-dnd-key', []);

  // Column width calculations - deferring render until measurements are complete
  const { containerRef, boardRef, columnWidth, shouldScroll, measurementComplete } =
    useDndColumn(columns);

  // Don't render anything until measurements are complete
  if (!measurementComplete || !columnWidth) {
    return <div className="h-full w-full bg-[var(--content-bg-color)]" ref={containerRef}></div>;
  }

  // Handle columns loading state
  if (isColumnsLoading && !columns.length) {
    return (
      <div
        className="h-full w-full bg-[var(--content-bg-color)] flex items-center justify-center"
        ref={containerRef}
      >
        <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
      </div>
    );
  }

  // Handle columns error state
  if (isColumnsError) {
    return (
      <div
        className="h-full w-full bg-[var(--content-bg-color)] flex flex-col items-center justify-center p-4 text-center"
        ref={containerRef}
      >
        <AlertCircle className="w-8 h-8 text-red-500 mb-2" />
        <p className="text-sm text-red-500">Failed to load task columns</p>
      </div>
    );
  }

  // If sorting by deadline is enabled, use the specialized read-only board
  if (sortByDeadline) {
    return (
      <DeadlineSortedTaskBoard
        topicId={topicId}
        projectId={projectId}
        tasks={tasks}
        hasCollaborators={hasCollaborators}
        showUnscheduledOnly={showUnscheduledOnly}
        showUnassignedOnly={showUnassignedOnly}
        userFilters={userFilters}
      />
    );
  }

  // Regular drag-and-drop enabled task board
  return (
    <>
      <DndContext
        key={dndKey}
        sensors={sensors}
        collisionDetection={args => {
          // First try with pointerWithin (most accurate for user interaction)
          const pointerCollisions = pointerWithin(args);
          if (pointerCollisions.length) {
            return pointerCollisions;
          }

          // Then try rectIntersection (most generous)
          const rectCollisions = rectIntersection(args);
          if (rectCollisions.length) {
            return rectCollisions;
          }

          // Finally, fall back to closestCenter (best compromise)
          return closestCenter(args);
        }}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        autoScroll={true}
        measuring={{
          droppable: {
            strategy: MeasuringStrategy.BeforeDragging,
          },
        }}
      >
        <TaskBoardLayout
          containerRef={containerRef}
          boardRef={boardRef}
          shouldScroll={shouldScroll}
          measurementComplete={measurementComplete}
          columnWidth={columnWidth}
          columns={columns}
          columnToDelete={columnToDelete}
          onDeleteColumn={handleDeleteColumn}
          onCancelDelete={() => setColumnToDelete(null)}
        >
          {/* Render all columns */}
          {columns.map(column => (
            <DroppableTaskColumnContainer
              key={column.id}
              column={column}
              items={filteredTasks.filter(task => task.status === column.status)}
              className={columnWidth}
              topicId={topicId}
              isRecording={isRecording && recordingColumn === column.status}
              isCreating={isCreating}
              activeColumn={activeColumn}
              title={newTaskTitle}
              description={newTaskDescription}
              tags={newTaskTags}
              deadline={newTaskDeadline}
              assignedTo={newTaskAssignedTo}
              showDescription={showDescription}
              inputRef={inputRef}
              itemToDelete={itemToDelete}
              isEditingColumnName={isEditingColumnName}
              isEditingColumnColor={isEditingColumnColor}
              totalTaskCount={filteredTasks.length}
              activeTask={activeTaskId}
              projectId={projectId}
              hasCollaborators={hasCollaborators}
              // Pass filter props for empty state
              showUnscheduledOnly={showUnscheduledOnly}
              showUnassignedOnly={showUnassignedOnly}
              userFilters={userFilters}
              onVoiceClick={() => handleVoiceInput(column.status)}
              onAddClick={() => {
                setActiveColumn(column.status);
                setIsCreating(true);
                setShowDescription(false);
                setTimeout(() => inputRef.current?.focus(), 0);
              }}
              onDelete={handleTaskDeletion}
              onDeleteClick={setItemToDelete}
              onCancelDelete={() => setItemToDelete(null)}
              onUpdateTask={handleTaskUpdate}
              onTitleChange={setNewTaskTitle}
              onDescriptionChange={setNewTaskDescription}
              onTagsChange={setNewTaskTags}
              onDeadlineChange={setNewTaskDeadline}
              onAssigneeChange={setNewTaskAssignedTo}
              onSubmit={() => {
                if (newTaskTitle) {
                  handleSubmit(column.status);
                } else {
                  logger.error('Cannot submit task with empty title');
                }
              }}
              onCancel={() => {
                setIsCreating(false);
                setShowDescription(false);
                setNewTaskAssignedTo(null); // Reset assignee on cancel
              }}
              moveTask={moveTask}
              moveTaskBetweenColumns={moveTaskBetweenColumns}
              onEditColumnNameClick={id => {
                setIsEditingColumnName(id);

                if (activeTaskId) {
                  const targetColumn = columns.find(c => c.id === id);
                  if (targetColumn) {
                    setTargetDropColumnId(id);
                    setDragOverColumn(targetColumn.status);
                  }
                }
              }}
              onEditColumnName={handleEditColumnName}
              onDeleteColumnClick={id => setColumnToDelete(id)}
              onColumnColorSelect={handleColumnColorChange}
              onAddColumnAfter={handleAddColumnAfter}
              onMoveColumnLeft={handleMoveColumnLeft}
              onMoveColumnRight={handleMoveColumnRight}
              forceTaskMove={forceTaskMove}
            />
          ))}
        </TaskBoardLayout>

        {/* Drag Overlay - shows a preview of the task being dragged */}
        <DragOverlay adjustScale={false} zIndex={100} dropAnimation={dragOverlay.dropAnimation}>
          {dragOverlay.renderOverlay()}
        </DragOverlay>
      </DndContext>
    </>
  );
};
